import { APIControl } from "./APIControl";
import { HitApi } from "../services/hitQuery/api";
import { Configuration } from "../services/hitQuery/configuration";

const ROUTE_HIT_RULES = [
  { pattern: /^\/watchlist$/i, name: "watchlist" },
  { pattern: /^\/standouts$/i, name: "standouts" },
  { pattern: /^\/profile$/i, name: "profile" },
  { pattern: /^\/marketsummary$/i, name: "marketSummary" },
  { pattern: /^\/indexanalysis$/i, name: "indexAnalysis" },
  { pattern: /^\/stockindexeffect$/i, name: "stockIndexEffect" },
  { pattern: /^\/globalindices$/i, name: "globalIndices" },
  { pattern: /^\/news$/i, name: "news" },
  {
    pattern: /^\/technicalanalysis(?:\/[^/]+)?$/i,
    name: "technicalAnalysis",
  },
  { pattern: /^\/screener$/i, name: "screener" },
  { pattern: /^\/stockfiltering$/i, name: "stockFiltering" },
  { pattern: /^\/personalsorting$/i, name: "personalSorting" },
  { pattern: /^\/portfolio$/i, name: "Portfolio" },
  {
    pattern: /^\/dividends\/dividendcalendar$/i,
    name: "dividends/dividendCalendar",
  },
  {
    pattern: /^\/dividends\/dividendexpectations$/i,
    name: "dividends/dividendExpectations",
  },
  {
    pattern: /^\/dividends\/dividendfiltering$/i,
    name: "dividends/dividendFiltering",
  },
  {
    pattern: /^\/dividends\/dividendcalculator$/i,
    name: "dividends/dividendCalculator",
  },
  {
    pattern: /^\/dividends\/calculator$/i,
    name: "dividends/Calculator",
  },
  { pattern: /^\/stockanalysis$/i, name: "StockAnalysis" },
  {
    pattern: /^\/stockanalysis\/summary\/[^/]+$/i,
    name: "StockAnalysis/summary",
  },
  {
    pattern: /^\/stockanalysis\/indicators\/[^/]+$/i,
    name: "StockAnalysis/indicators",
  },
  {
    pattern: /^\/stockanalysis\/financialstatement\/[^/]+$/i,
    name: "StockAnalysis/financialStatement",
  },
  {
    pattern: /^\/stockanalysis\/ratio\/[^/]+$/i,
    name: "StockAnalysis/ratio",
  },
  {
    pattern: /^\/stockanalysis\/compare\/[^/]+$/i,
    name: "StockAnalysis/compare",
  },
  {
    pattern: /^\/stockanalysis\/dividend\/[^/]+$/i,
    name: "StockAnalysis/dividend",
  },
  {
    pattern: /^\/stockanalysis\/affiliatespartnerships\/[^/]+$/i,
    name: "StockAnalysis/affiliatesPartnerships",
  },
  {
    pattern: /^\/stockanalysis\/news\/[^/]+$/i,
    name: "StockAnalysis/news",
  },
  {
    pattern: /^\/stockanalysis\/report\/[^/]+$/i,
    name: "StockAnalysis/report",
  },
];

const NON_TRACKED_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/auth(?:\/|$)/i,
  /^\/resetpassword\/?$/i,
];

const basePath = process.env.REACT_APP_HIT_QUERY_API_BASE_PATH;
const hitApi = new HitApi(
  new Configuration({ basePath }),
  undefined,
  APIControl(basePath)
);

const TRANSIENT_HIT_ERROR_PATTERN =
  /StatusCode="?Unavailable|Error connecting to subchannel|TaskCanceledException/i;
const TRANSIENT_RETRY_DELAY_MS = 400;

const wait = (delay) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });

export const getPageViewName = (pathname) => {
  const normalizedPathname = `/${String(pathname ?? "")
    .split("?")[0]
    .split("#")[0]
    .split("/")
    .filter(Boolean)
    .join("/")}`;

  if (
    NON_TRACKED_ROUTE_PATTERNS.some((pattern) =>
      pattern.test(normalizedPathname)
    )
  ) {
    return null;
  }

  const routeRule = ROUTE_HIT_RULES.find(({ pattern }) =>
    pattern.test(normalizedPathname)
  );

  return routeRule?.name || "notFound";
};

export const sendHitRequest = ({
  name,
  type = "pageView",
  token = localStorage.getItem("token"),
  accountId = localStorage.getItem("accountId"),
}) => {
  const normalizedToken = String(token ?? "").trim();
  const normalizedAccountId = String(accountId ?? "").trim();
  const normalizedName = String(name ?? "").trim();
  const normalizedType = String(type ?? "").trim();

  if (
    !normalizedToken ||
    !normalizedAccountId ||
    !normalizedName ||
    !normalizedType
  ) {
    return Promise.resolve();
  }

  const hitRequest = {
    AccountId: normalizedAccountId,
    Platform: "HisselabWeb",
    Items: [
      {
        Type: normalizedType,
        Name: normalizedName,
      },
    ],
  };

  const sendRequest = (attempt = 1) => {
    const startedAt = performance.now();

    return hitApi.apiHitHitPost(normalizedToken, hitRequest).then((response) => {
      const apiStatus = response.data?.Status;
      const apiError = response.data?.Error;
      const isTransientError =
        apiStatus !== "Ok" && TRANSIENT_HIT_ERROR_PATTERN.test(apiError || "");

      console.table(
        hitRequest.Items.map((item) => ({
          AccountId: hitRequest.AccountId,
          Platform: hitRequest.Platform,
          Type: item.Type,
          Name: item.Name,
          HttpStatus: response.status,
          ApiStatus: apiStatus,
          ApiError: apiError || "",
          Attempt: attempt,
          DurationMs: Math.round(performance.now() - startedAt),
          KongRequestId: response.headers?.["x-kong-request-id"] || "",
          IdentityTokenLength: normalizedToken.length,
        }))
      );

      if (isTransientError && attempt === 1) {
        console.warn("Hit API is temporarily unavailable. Retrying once.", {
          name: normalizedName,
          kongRequestId: response.headers?.["x-kong-request-id"],
        });

        return wait(TRANSIENT_RETRY_DELAY_MS).then(() => sendRequest(attempt + 1));
      }

      if (apiStatus !== "Ok") {
        console.error("Hit API returned an application error.", {
          httpStatus: response.status,
          apiStatus,
          apiError,
          request: hitRequest,
          attempt,
          kongRequestId: response.headers?.["x-kong-request-id"],
        });
      }

      return response;
    });
  };

  return sendRequest().catch((error) => {
    console.error("Hit could not be sent.", error);
  });
};
